import React, { ReactElement, useContext, useMemo } from "react";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { SignalsPageContext } from "@shared/features/signals/common/SignalsPage.context";
import styled from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import NewLookTradesTableWrapper from "features/trades/NewLook/NewLookTradesTableWrapper.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useSearchInput from "utils/hooks/useSearchInput";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";

interface NewLookSignalsListProps {
  /** The signals table; receives `searchFilter` like in SmartTableLayout. */
  tableComponent: ReactElement<{ searchFilter?: string }>;
}

/**
 * New-look signals list: one panel, the selected group as its title with the
 * number of signals and the search in the header, the table edge to edge
 * (the same table treatment as the trades list), empty states inside.
 */
const NewLookSignalsList = ({ tableComponent }: NewLookSignalsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tSignals } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { useMobileSearch, search: headerSearch } =
    useContext(PageWrapperContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const {
    subGroupId,
    signalsData,
    isSignalsLoading,
    getGroupsDataStatus,
    getAdminSubGroupsStatus,
  } = useContext(SignalsPageContext)!;

  const [searchInput, searchInputValue] = useSearchInput({
    placeholder: t("labels.search_placeholder"),
    style: { width: 220 },
    lowerCaseValue: true,
  });
  const searchValue = !useMobileSearch
    ? searchInputValue
    : isMobile
    ? headerSearch.value
    : "";

  const groupName = useMemo(() => {
    const groups = [
      ...((getGroupsDataStatus.data as { id: number; name: string }[]) || []),
      ...((getAdminSubGroupsStatus.data as { id: number; name: string }[]) ||
        []),
    ];
    return groups.find((group) => group.id === subGroupId)?.name;
  }, [getGroupsDataStatus.data, getAdminSubGroupsStatus.data, subGroupId]);

  // Without a group or any signals there is nothing to search.
  const showSearch = !useMobileSearch && (!!groupName || !!signalsData?.length);

  return (
    <ListPanel
      title={groupName || tSignals("page.list_title")}
      count={groupName && !isSignalsLoading ? signalsData?.length : undefined}
      actions={showSearch ? searchInput : undefined}
      flush={true}
    >
      <NewLookTradesTableWrapper $flush={true}>
        {React.cloneElement(tableComponent, { searchFilter: searchValue })}
      </NewLookTradesTableWrapper>
    </ListPanel>
  );
};

const ListPanel = styled(Panel)`
  overflow: hidden;
`;

export default NewLookSignalsList;
